import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { api } from '@/api';
import { toast } from 'sonner';
import {
  Package, FileText, Upload, Download, Clock, CheckCircle2, XCircle, AlertTriangle,
  Calendar, Building2, Loader2, RefreshCw, ChevronRight, Eye, File
} from 'lucide-react';

const STATUS_COLORS = {
  'Draft': 'bg-slate-100 text-slate-700',
  'In Preparation': 'bg-blue-100 text-blue-700',
  'Submitted to Contractor': 'bg-purple-100 text-purple-700',
  'Returned by Contractor': 'bg-orange-100 text-orange-700',
  'Submitted to Consultant': 'bg-indigo-100 text-indigo-700',
  'Under Consultant Review': 'bg-cyan-100 text-cyan-700',
  'Approved': 'bg-green-100 text-green-700',
  'Approved with Comments': 'bg-emerald-100 text-emerald-700',
  'Rejected': 'bg-red-100 text-red-700',
  'On Hold': 'bg-amber-100 text-amber-700'
};

const SubcontractorPortal = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [portalData, setPortalData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Upload state
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileRole, setFileRole] = useState('Shop Drawing');
  const [fileDescription, setFileDescription] = useState('');
  const [selectedCycleId, setSelectedCycleId] = useState('');

  const loadPortalData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/shop-drawings/public/subcontractor-portal/${token}`);
      setPortalData(response.data);
      
      // Set default cycle for upload
      if (response.data.cycles?.length > 0) {
        const latestCycle = response.data.cycles[response.data.cycles.length - 1];
        setSelectedCycleId(latestCycle.id);
      }
    } catch (err) {
      console.error('Portal error:', err);
      setError(err.response?.data?.detail || 'Failed to load portal. The link may be invalid or expired.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadPortalData();
  }, [loadPortalData]);

  const handleUpload = async () => {
    if (!selectedFile || !selectedCycleId) {
      toast.error('Please select a file and cycle');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('cycle_id', selectedCycleId);
      formData.append('file_role', fileRole);
      formData.append('description', fileDescription);

      await api.post(`/shop-drawings/public/subcontractor-portal/${token}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('File uploaded successfully');
      setUploadDialogOpen(false);
      setSelectedFile(null);
      setFileDescription('');
      loadPortalData();
    } catch (err) {
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (fileId, filename) => {
    try {
      const response = await api.get(
        `/shop-drawings/public/subcontractor-portal/${token}/download/${fileId}`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      toast.error('Failed to download file');
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-indigo-600 mx-auto mb-4" />
          <p className="text-slate-600">Loading portal...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="h-16 w-16 text-amber-500 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-slate-900 mb-2">Portal Access Error</h1>
            <p className="text-slate-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { package: pkg, cycles, project, subcontractor_name, expires_at } = portalData;
  const currentCycle = cycles?.find(c => c.id === pkg?.current_cycle_id) || cycles?.[cycles.length - 1];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-indigo-900 text-white">
        <div className="max-w-6xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <Package className="h-8 w-8 sm:h-10 sm:w-10 shrink-0" />
              <div className="min-w-0">
                <h1 className="text-lg sm:text-2xl font-bold truncate">Shop Drawing Portal</h1>
                <p className="text-indigo-200 text-sm sm:text-base truncate">{subcontractor_name}</p>
              </div>
            </div>
            <Badge className="bg-indigo-700 text-white shrink-0 text-xs sm:text-sm">
              Expires: {formatDate(expires_at)}
            </Badge>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        <p className="text-xs sm:text-sm text-slate-500">Use this portal to review your package details, upload submission files, and track revision history for the selected project.</p>
        {/* Package Info Card */}
        <Card className="border-indigo-200" data-testid="portal-package-card">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <h2 className="text-xl font-bold text-slate-900">{pkg?.package_ref}</h2>
                  <Badge className={STATUS_COLORS[pkg?.status] || ''}>
                    {pkg?.status}
                  </Badge>
                  <Badge variant="outline">Revision {pkg?.current_cycle_number || 0}</Badge>
                </div>
                <p className="text-slate-600 mb-2">{pkg?.title}</p>
                <div className="flex items-center gap-4 text-sm text-slate-500">
                  <span><Building2 className="h-4 w-4 inline mr-1" />{pkg?.trade}</span>
                  <span><Calendar className="h-4 w-4 inline mr-1" />{project?.name}</span>
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={loadPortalData} className="rounded-sm">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
                <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="rounded-sm bg-indigo-600 hover:bg-indigo-700" data-testid="upload-files-btn">
                      <Upload className="h-4 w-4 mr-2" />
                      Upload Files
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Upload Shop Drawing</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label className="text-xs font-bold uppercase tracking-wider">Submission Cycle</Label>
                        <Select value={selectedCycleId} onValueChange={setSelectedCycleId}>
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Select cycle" />
                          </SelectTrigger>
                          <SelectContent>
                            {cycles?.map(c => (
                              <SelectItem key={c.id} value={c.id}>
                                Revision {c.cycle_number}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs font-bold uppercase tracking-wider">File Type</Label>
                        <Select value={fileRole} onValueChange={setFileRole}>
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Shop Drawing">Shop Drawing</SelectItem>
                            <SelectItem value="Sample">Sample</SelectItem>
                            <SelectItem value="Specification">Specification</SelectItem>
                            <SelectItem value="Supporting Document">Supporting Document</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs font-bold uppercase tracking-wider">File</Label>
                        <Input
                          type="file"
                          onChange={(e) => setSelectedFile(e.target.files?.[0])}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs font-bold uppercase tracking-wider">Description (Optional)</Label>
                        <Input
                          value={fileDescription}
                          onChange={(e) => setFileDescription(e.target.value)}
                          placeholder="Brief description of the file"
                          className="mt-1"
                        />
                      </div>
                    </div>
                    <DialogFooter className="mt-4">
                      <Button variant="outline" onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
                      <Button onClick={handleUpload} disabled={uploading || !selectedFile}>
                        {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                        Upload
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-slate-100">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="revisions">Revisions ({cycles?.length || 0})</TabsTrigger>
            <TabsTrigger value="files">All Files</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Current Status */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm font-bold uppercase tracking-wider">Current Status</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded">
                    <span className="text-sm text-slate-600">Package Status</span>
                    <Badge className={STATUS_COLORS[pkg?.status] || ''}>{pkg?.status}</Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded">
                    <span className="text-sm text-slate-600">Current Revision</span>
                    <span className="font-bold">{pkg?.current_cycle_number || 0}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded">
                    <span className="text-sm text-slate-600">Total Submissions</span>
                    <span className="font-bold">{pkg?.total_cycles || 0}</span>
                  </div>
                  {pkg?.required_on_site_date && (
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded">
                      <span className="text-sm text-slate-600">Required On Site</span>
                      <span className="font-bold">{formatDate(pkg.required_on_site_date)}</span>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Latest Cycle Info */}
              {currentCycle && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm font-bold uppercase tracking-wider">
                      Latest Submission (Rev {currentCycle.cycle_number})
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {currentCycle.submitted_to_consultant_date && (
                      <div className="flex items-center justify-between p-3 bg-slate-50 rounded">
                        <span className="text-sm text-slate-600">Submitted to Consultant</span>
                        <span className="font-medium">{formatDate(currentCycle.submitted_to_consultant_date)}</span>
                      </div>
                    )}
                    {currentCycle.consultant_response_date && (
                      <div className="flex items-center justify-between p-3 bg-slate-50 rounded">
                        <span className="text-sm text-slate-600">Consultant Response</span>
                        <span className="font-medium">{formatDate(currentCycle.consultant_response_date)}</span>
                      </div>
                    )}
                    {currentCycle.consultant_decision && (
                      <div className="flex items-center justify-between p-3 bg-slate-50 rounded">
                        <span className="text-sm text-slate-600">Decision</span>
                        <Badge className={
                          currentCycle.consultant_decision === 'Approved' ? 'bg-green-100 text-green-700' :
                          currentCycle.consultant_decision === 'Approved with Comments' ? 'bg-emerald-100 text-emerald-700' :
                          currentCycle.consultant_decision === 'Rejected' ? 'bg-red-100 text-red-700' :
                          'bg-slate-100 text-slate-700'
                        }>
                          {currentCycle.consultant_decision}
                        </Badge>
                      </div>
                    )}
                    {currentCycle.consultant_comment_summary && (
                      <div className="p-3 bg-amber-50 rounded border border-amber-200">
                        <p className="text-xs font-bold uppercase tracking-wider text-amber-700 mb-1">Consultant Comments</p>
                        <p className="text-sm text-slate-700">{currentCycle.consultant_comment_summary}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Revisions Tab */}
          <TabsContent value="revisions" className="mt-4">
            <div className="space-y-4">
              {cycles?.map((cycle, idx) => (
                <Card key={cycle.id} className={cycle.id === currentCycle?.id ? 'border-indigo-300 ring-1 ring-indigo-200' : ''}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <Badge variant="outline" className="text-lg px-3 py-1">Rev {cycle.cycle_number}</Badge>
                        {cycle.id === currentCycle?.id && (
                          <Badge className="bg-indigo-100 text-indigo-700">Current</Badge>
                        )}
                        {cycle.consultant_decision && (
                          <Badge className={
                            cycle.consultant_decision === 'Approved' ? 'bg-green-100 text-green-700' :
                            cycle.consultant_decision === 'Approved with Comments' ? 'bg-emerald-100 text-emerald-700' :
                            cycle.consultant_decision === 'Rejected' ? 'bg-red-100 text-red-700' :
                            'bg-slate-100 text-slate-700'
                          }>
                            {cycle.consultant_decision}
                          </Badge>
                        )}
                      </div>
                      <span className="text-sm text-slate-500">
                        {cycle.files?.length || 0} files
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 text-sm">
                      <div>
                        <p className="text-slate-500">Submitted</p>
                        <p className="font-medium">{formatDate(cycle.submitted_to_consultant_date) || 'Not yet'}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Response</p>
                        <p className="font-medium">{formatDate(cycle.consultant_response_date) || 'Pending'}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Review Days</p>
                        <p className="font-medium">{cycle.days_in_consultant_review || 0} days</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Status</p>
                        <p className="font-medium">{cycle.status || 'N/A'}</p>
                      </div>
                    </div>

                    {/* Files for this cycle */}
                    {cycle.files?.length > 0 && (
                      <div className="mt-4 pt-4 border-t">
                        <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Files</p>
                        <div className="space-y-2">
                          {cycle.files.map(file => (
                            <div key={file.id} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                              <div className="flex items-center gap-2">
                                <File className="h-4 w-4 text-slate-400" />
                                <span className="text-sm">{file.original_filename}</span>
                                <Badge variant="outline" className="text-xs">{file.file_role}</Badge>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDownload(file.id, file.original_filename)}
                              >
                                <Download className="h-4 w-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Files Tab */}
          <TabsContent value="files" className="mt-4">
            <Card>
              <CardContent className="p-4">
                {cycles?.flatMap(c => c.files || []).length > 0 ? (
                  <div className="space-y-2">
                    {cycles.flatMap(cycle => 
                      (cycle.files || []).map(file => (
                        <div key={file.id} className="flex items-center justify-between p-3 bg-slate-50 rounded hover:bg-slate-100">
                          <div className="flex items-center gap-3">
                            <File className="h-5 w-5 text-slate-400" />
                            <div>
                              <p className="font-medium">{file.original_filename}</p>
                              <p className="text-xs text-slate-500">
                                Rev {cycle.cycle_number} • {file.file_role} • {formatDate(file.uploaded_at)}
                              </p>
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDownload(file.id, file.original_filename)}
                          >
                            <Download className="h-4 w-4 mr-1" />
                            Download
                          </Button>
                        </div>
                      ))
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-500">
                    <FileText className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                    <p>No files uploaded yet</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Footer */}
      <div className="bg-slate-100 border-t mt-12">
        <div className="max-w-6xl mx-auto px-4 py-4 text-center text-sm text-slate-500">
          <p>Shop Drawing Portal • EOT Claims Hub</p>
        </div>
      </div>
    </div>
  );
};

export default SubcontractorPortal;
